import { useEffect, useState } from 'react';
import { QuizManager } from '../../lib/modules/quiz-manager';
import { initMetaPixel } from '../../lib/shared/meta-tracker';
import { EMAIL_SLIDE, START_SLIDE } from './quiz-component-types';

function getExpiryTimestamp() {
    const date = new Date();

    date.setSeconds(date.getSeconds() + 600);

    return date.getTime();
}

function getInitialSlideIndexFromUrl(slides) {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('slideIndex');

    if (raw === null) {
        return undefined;
    }

    const index = parseInt(raw, 10);
    const emailSlideIndex = slides.findIndex(slide => slide.component === EMAIL_SLIDE);
    const maxAllowed = emailSlideIndex === -1 ? slides.length - 1 : emailSlideIndex;

    if (isNaN(index) || index < 0 || index > maxAllowed) {
        return 0;
    }

    return index;
}

const useQuiz = (slides, version) => {
    const [quiz] = useState(() => new QuizManager(slides, version, getInitialSlideIndexFromUrl(slides)));
    const [currentSlideIndex, setCurrentSlideIndex] = useState(quiz.getCurrentSlideIndex());
    const [finished, setFinished] = useState(quiz.finished);
    const [results, setResults] = useState(quiz.getResults());
    const [temporaryAnswer, setTemporaryAnswer] = useState({});
    const currentSlide = finished ? {} : slides[currentSlideIndex];
    const startSlideIndex = slides.findIndex(slide => slide.component === START_SLIDE);

    useEffect(() => {
        if (startSlideIndex >= 0 && currentSlideIndex > startSlideIndex) {
            initMetaPixel(version);
        }
    }, [currentSlideIndex, startSlideIndex, version]);

    const syncResults = () => {
        quiz.saveState();
        setResults(quiz.getResults());
    };

    const submitAnswer = answer => {
        quiz.submitAnswer(answer);

        if (quiz.finished) {
            setFinished(true);
        } else {
            setCurrentSlideIndex(quiz.getCurrentSlideIndex());
            setTemporaryAnswer({});
        }

        syncResults();
    };

    const submitTemporaryAnswer = answer => setTemporaryAnswer(answer);

    const nextSlide = answer => {
        if (answer?.questionId) {
            quiz.submitAnswer(answer);
        } else {
            quiz.nextSlide();
        }

        if (quiz.finished) {
            setFinished(true);
        } else {
            setCurrentSlideIndex(quiz.getCurrentSlideIndex());
            setTemporaryAnswer({});
        }

        syncResults();
    };

    const backSlide = () => {
        const lastUserResponse = quiz.backSlide();

        setCurrentSlideIndex(quiz.getCurrentSlideIndex());
        setTemporaryAnswer(lastUserResponse);
        syncResults();
    };

    const setEmail = email => {
        quiz.setEmail(email);
        syncResults();
    };

    const setExpiryTimestampPromo = () => {
        quiz.setExpiryTimestampPromo(getExpiryTimestamp());
        syncResults();
    };

    return {
        currentSlideIndex,
        currentSlide,
        finished,
        results,
        submitAnswer,
        temporaryAnswer,
        submitTemporaryAnswer,
        nextSlide,
        backSlide,
        setEmail,
        setExpiryTimestampPromo
    };
};

export {
    useQuiz
};
